import { Box, Image, Stack, useColorModeValue, useTheme, Tooltip, useColorMode, Text, Link } from "@chakra-ui/react";
import React, { useCallback } from "react";
import type { DealTileProps } from "../../../types/dealfinder";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { PurchaseDealModal } from "./modals/PurchaseDealModal";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { DealMenuButtons } from "./DealMenuButtons";
import { useInvestmentDetailModal } from "../../../hooks/useInvestmentDetailModal";
import { useEntitlementKey, useTileProps, useUserPreference } from "hooks";
import { MarkdownViewer } from "screens/markdown/MarkdownViewer";
import deal_placeholder from "screens/common/static/images/deal_placeholder_banner.svg";
import deal_placeholder_dark from "screens/common/static/images/deal_placeholder_banner_dark.svg";
import { useFocusBadgeColors } from "screens/collection/views/AutocompleteSuggestion";
import { useSelector, shallowEqual } from "react-redux";
import type { RootState } from "state/rootReducer";
import { getInvestmentRatingColor } from "./utils/dealUtils";
import { getCountryCode } from "./utils/countryCodeUtils";
import { useCopyValue } from "hooks/useCopies";
import type { DealPromoBanner } from "types/copy";

export const DealTile: React.FC<DealTileProps> = ({ deal, displayOption = "tile" }) => {
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const bgColor = useColorModeValue("white", "gray.700");
  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  const revenueBgColor = useColorModeValue("gray.100", "gray.600");
  const { currentDealId, openModal, closeModal, currentExchangeTicker } = useInvestmentDetailModal();
  const isOpen = currentDealId === deal.id || currentExchangeTicker === `${deal.exchange}:${deal.symbol}`.toLowerCase();
  const injectDebugEntityPreference = useUserPreference("ui_inject_debug_entity");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const theme = useTheme();
  const commonTileProps = useTileProps();
  const fontColor = useColorModeValue("gray.700", "gray.400");
  const { colorMode } = useColorMode();
  const purchasedTagBg = useColorModeValue("green.200", "green.800");
  const purchasedTagColor = useColorModeValue("green.800", "green.100");
  const comingSoonTagBg = useColorModeValue("teal.200", "blue.800");
  const comingSoonTagFontColor = useColorModeValue("gray.800", "blue.800");
  const hasDealFinderPurchase = useEntitlementKey("ui_enable_dealfinder_purchase");
  const colors = useFocusBadgeColors();
  const isAnonymousUser = useSelector((state: RootState) => state.session.isAnonymous, shallowEqual);
  const rawDealFinderPromo = useCopyValue("copy_deal_finder_promotion_banner");
  const ratingTooltip = useCopyValue("copy_deal_finder_rating_tooltip");
  const dealFinderPromo: DealPromoBanner | null = rawDealFinderPromo.length > 0 ? JSON.parse(rawDealFinderPromo[0]) : null;
  const dealFinderPromoBgColor = useColorModeValue(dealFinderPromo?.bgColor || "yellow.500", "gray.300");
  const dealFinderPromoTextColor = useColorModeValue(dealFinderPromo?.textColor || "black", "white");

  const handleTileClick = useCallback(() => {
    if (injectDebugEntityPreference) {
      console.log(`Deal Data:`, deal);
    }
    openModal(deal);
  }, [injectDebugEntityPreference, openModal, deal]);

  // Render a more compact list view
  if (displayOption === "list") {
    return (
      <>
        <Box
          {...commonTileProps}
          border={"none"}
          borderRadius={"none"}
          color={fontColor}
          className={`ch-dealfinder-list-item ch-dealfinder-deal-${deal.id}`}
          overflow="hidden"
          cursor="pointer"
          onClick={handleTileClick}
          py="1rem"
          pl="1rem"
          pr="0"
          _hover={{
            bg: hoverBgColor,
          }}>
          <Stack spacing="1rem" direction="row" justifyContent="space-between">
            <Box width="16rem" alignContent={"center"}>
              {deal.companyLogoUrl ? (
                <Image src={deal.companyLogoUrl} alt="Company logo" height="20px" objectFit="contain" />
              ) : (
                <TextOverflowTooltip label={deal.name || ""} style={{ fontWeight: 500, fontFamily: theme.fonts.logo }} fontSize="1.2rem" />
              )}
            </Box>
            <Box width="100%">
              <TextOverflowTooltip
                className="ch-project-company-name"
                style={{ fontWeight: 500 }}
                fontSize="sm"
                breakWord={false}
                label={deal.name}
              />
              <TextOverflowTooltip
                className="ch-project-company-stock"
                color={textColor}
                label={`${deal.exchange}:${deal.symbol}`}
                fontSize="xs"
              />
            </Box>
            <Box alignContent={"center"}>
              <DealMenuButtons deal={deal} iconSize="1rem" showBorder={false} displayOption={displayOption} />
            </Box>
          </Stack>
        </Box>

        <PurchaseDealModal isOpen={isOpen} onClose={closeModal} deal={deal} />
      </>
    );
  }

  return (
    <>
      <Box
        className={`ch-dealfinder-tile ch-dealfinder-deal-${deal.id}`}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        bg={bgColor}
        transition="all 0.3s"
        height="100%"
        display="flex"
        flexDirection="column"
        cursor="pointer"
        onClick={handleTileClick}
        position="relative"
        _hover={{
          transform: "translateY(-4px)",
          shadow: "lg",
        }}>
        {deal.status === "pending" && (
          <Box
            position="absolute"
            top="20px"
            left="-33px"
            zIndex="2"
            width="130px"
            height="26px"
            bg={comingSoonTagBg}
            transform="rotate(-45deg)"
            transformOrigin="center"
            boxShadow="0 6px 8px rgba(0,0,0,0.2)"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Text fontSize="10px" fontWeight="bold" color={comingSoonTagFontColor} whiteSpace="nowrap">
              COMING SOON
            </Text>
          </Box>
        )}
        {deal.status !== "pending" && dealFinderPromo && dealFinderPromo.tierFilter && deal.pricingTier === dealFinderPromo.tierFilter && (
          <Box
            position="absolute"
            top="10px"
            left="-22px"
            zIndex="2"
            width="88px"
            height="24px"
            bg={dealFinderPromoBgColor}
            transform="rotate(-45deg)"
            transformOrigin="center"
            boxShadow="0 6px 8px rgba(0,0,0,0.2)"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Text fontSize="10px" fontWeight="bold" color={dealFinderPromoTextColor} whiteSpace="nowrap">
              {dealFinderPromo.label}
            </Text>
          </Box>
        )}
        <Stack spacing={0} flex={1}>
          <Box position={"relative"} p={4} minH="2.5rem" display="flex" alignItems="center">
            {deal.companyLogoUrl && deal.companyLogoUrl.startsWith("https://storage.googleapis.com") ? (
              <Tooltip label={deal.name} placement="top">
                <Image src={deal.companyLogoUrl} alt="Company logo" height="30px" maxW="calc(100% - 2rem)" objectFit="contain" />
              </Tooltip>
            ) : (
              <TextOverflowTooltip
                className="ch-dealfinder-tile-title"
                label={deal.name || ""}
                breakWord={false}
                style={{ fontSize: "1rem", fontWeight: 400, width: "calc(100% - 2rem)", lineHeight: "29px" }}
              />
            )}
            {deal.investmentRating !== null && (
              <Tooltip label={ratingTooltip} placement="left" maxWidth={"200px"}>
                <Box
                  position="absolute"
                  top=".9rem"
                  right=".5rem"
                  zIndex="1"
                  className="ch-dealfinder-tile-purchased"
                  fontSize={"16px"}
                  fontWeight={"semibold"}
                  borderRadius={"md"}
                  px="5px"
                  py="5px"
                  bg={getInvestmentRatingColor(deal.investmentRating)}
                  color={textColor}>
                  {deal.investmentRating.toFixed(1)}
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box position="relative" overflow="hidden" borderTopWidth="1px" borderTopColor={revenueBgColor}>
            <Image
              src={deal.companyBannerUrl || undefined}
              alt={deal.name}
              objectFit="cover"
              objectPosition="right center"
              width="100%"
              height="8rem"
              transition="transform 0.3s"
              _groupHover={{ transform: "scale(1.05)" }}
              fallback={
                <Image
                  src={colorMode === "dark" ? deal_placeholder_dark : deal_placeholder}
                  alt={deal.name || "Deal image"}
                  objectFit="cover"
                  objectPosition="right center"
                  width="100%"
                  height="8rem"
                  minH="8rem"
                />
              }
            />
            <Stack direction="row" spacing={"5px"} alignItems={"flex-end"} position="absolute" top="6.5rem" right=".5rem" zIndex="1">
              {deal.hasUserPurchased && (
                <Box
                  className="ch-dealfinder-tile-purchased"
                  fontSize={"10px"}
                  borderRadius={"md"}
                  px="5px"
                  py="1px"
                  bg={purchasedTagBg}
                  color={purchasedTagColor}>
                  Purchased
                </Box>
              )}
              {deal.type && (
                <Box
                  className="ch-dealfinder-tile-type"
                  fontSize={"9px"}
                  borderRadius={"md"}
                  px="5px"
                  py="1px"
                  bg={colors[deal.type.toUpperCase()]}
                  color={purchasedTagColor}>
                  {deal.type.toUpperCase()}
                </Box>
              )}
            </Stack>
          </Box>
          <Stack spacing={2} flex={1} alignItems="flex-start" height={"100%"} justifyContent={"space-between"}>
            <Box
              px="1rem"
              pt="0.5rem"
              alignContent={"top"}
              fontSize={"xs"}
              overflow={"hidden"}
              minH={hasDealFinderPurchase ? "7.5rem" : "unset"}>
              <Stack
                direction="column"
                alignItems="flex-start"
                width={"100%"}
                justifyContent="space-between"
                spacing={"0"}
                pb={hasDealFinderPurchase ? "0.5rem" : "unset"}>
                <Stack direction="row" alignItems="center" spacing={0} width="100%" justifyContent={"space-between"}>
                  <TextOverflowTooltip
                    className="ch-project-company-name"
                    label={deal.name || ""}
                    breakWord={false}
                    style={{ fontWeight: 600, width: deal.country ? "12rem" : "100%" }}
                    color={textColor}
                    fontSize="1rem"
                  />
                  {deal.country &&
                    (() => {
                      const countryCode = getCountryCode(deal.country);
                      return countryCode ? (
                        <Box zIndex="1" className="ch-dealfinder-tile-country-flag" width="24px">
                          <Image
                            src={`https://flagcdn.com/24x18/${countryCode}.png`}
                            alt={`${deal.country} flag`}
                            width="24px"
                            height="18px"
                            borderRadius="2px"
                            objectFit="cover"
                            fallback={
                              <Box
                                width="24px"
                                height="18px"
                                borderRadius="2px"
                                bg="gray.200"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="8px"
                                fontWeight="bold"
                                color="gray.600">
                                {countryCode.toUpperCase()}
                              </Box>
                            }
                          />
                        </Box>
                      ) : null;
                    })()}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} justify={"space-between"} width="100%">
                  <TextOverflowTooltip
                    className="ch-project-company-stock"
                    color={textColor}
                    style={{ fontWeight: 600, width: "100%" }}
                    label={`${deal.exchange}:${deal.symbol}`}
                    fontSize="11px"
                  />
                  {deal.companyUrl && (
                    <Link
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      display="inline-block"
                      width="100%"
                      textAlign={"right"}
                      href={
                        deal.companyUrl.startsWith("https://") || deal.companyUrl.startsWith("http://")
                          ? deal.companyUrl
                          : `https://${deal.companyUrl}`
                      }
                      isExternal
                      onClick={(e) => e.stopPropagation()}>
                      <Text as="span" color="blue.500" fontSize="xs" ml={1}>
                        {deal.companyUrl
                          .replace(/https?:\/\//, "")
                          .replace(/^www\./, "")
                          .replace(/\/$/, "")
                          .replace(/\/.*$/, "")}
                      </Text>
                    </Link>
                  )}
                </Stack>
              </Stack>
              <ShowMoreContainer maxHeight="3rem" minHeight="6rem" scrollingContainer>
                <MarkdownViewer
                  noBoldHeadings
                  backgroundColor={bgColor}
                  fontColor={textColor}
                  content={
                    deal.companyShortDescription ||
                    `A company overview for ${deal.name} will be generated by Charli and displayed here when complete.`
                  }
                  fontSize="xs"
                />
              </ShowMoreContainer>
              {deal.revenue ? (
                <Stack
                  bgColor={revenueBgColor}
                  px="5px"
                  borderRadius={"3px"}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  mt="0.5rem">
                  <Text>Revenue</Text>
                  <Text>{deal.revenue}</Text>
                </Stack>
              ) : (
                <Box height={"1.65rem"} />
              )}
            </Box>
            <DealMenuButtons deal={deal} isAnonymousUser={isAnonymousUser} />
          </Stack>
        </Stack>
      </Box>

      {isOpen && <PurchaseDealModal isOpen={isOpen} onClose={closeModal} deal={deal} />}
    </>
  );
};
